import { CommonModule, DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, Inject, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import {
  FormGroup,
  ReactiveFormsModule,
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  AutoRegistrationService,
  COUNTRY,
  ComponentBase,
  ErrorFormComponent,
  LanguageComponent,
  NumberOnlyDirective,
  SavePhasesDataType,
  SharedService,
} from 'src/app/core';
import { ToastrService } from 'ngx-toastr';
import { AutoRegistrationConfig } from '../models/auto-registration-config';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { finalize, takeUntil } from 'rxjs/operators';
import { VideoComponent } from 'src/app/shared/video/video.component';
import { SalesChatService } from 'src/app/core';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { TermsConditionsComponent } from './terms-conditions/terms-conditions.component';
import { VerificationComponent } from './verification/verification.component';
import { environment } from 'src/environments/environment';
import { CONFIG } from '../registration-config';

/** Matches Angular Flex Layout `lt-md` / `gt-sm` (form uses fxFlex.lt-md, slider uses fxShow.gt-sm). */
const VERIFICATION_INLINE_MAX_WIDTH = '(max-width: 959px)';

@Component({
  selector: 'app-sign-up',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    TranslateModule,
    MatSidenavModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatCheckboxModule,
    NumberOnlyDirective,
    ErrorFormComponent,
    LanguageComponent,
    VerificationComponent,
    ResetPasswordComponent,
    TermsConditionsComponent,
  ],
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
})
export class SignUpComponent extends ComponentBase implements OnInit, AfterViewInit, OnDestroy {
  /** Reference to the verification sidenav component */
  @ViewChild('verificationSidenav') verificationSidenav!: MatSidenav;

  /**
   * On small viewports, verification is shown in the main column with the same header as desktop
   * (logo, watch video, language) instead of the end sidenav overlay.
   */
  isSmallViewport = false;

  /** Controls password field visibility (show/hide) */
  hide: boolean = true;

  isVerifyRoute: boolean = false;

  /** Sign-up form group with all registration fields */
  form!: FormGroup;

  /** List of countries for country selector */
  countries: COUNTRY[] = [];

  /** Loader flag for phone country code while getCountries is in progress */
  countriesLoading = false;

  /** Current UI direction (ltr/rtl) from locale */
  dir: string = localStorage.getItem('lang');

  /** Data passed to verification component (RegistrationCycleLogId, user, verifyType) when opening after submit */
  verificationData: {
    RegistrationCycleLogId?: string;
    user?: string;
    verifyType?: any;
  } = {};

  /** When true, verification step (sidenav) is active */
  verifyStep: boolean = false;

  /** When true, terms & conditions (sidenav) is active */
  termsStep: boolean = false;

  /** When true, reset password (sidenav) is active */
  resetStep: boolean = false;

  /** Returns app domain from window.environment or environment config */
  get appDomain(): string {
    return ((window as any).environment || environment).AppDomain;
  }

  /** RegistrationCycleLogId for verification: from verificationData (after submit) or sharedService (init) */
  get inlineRegistrationCycleLogIdForVerification(): string | undefined {
    return (
      this.verificationData?.RegistrationCycleLogId ??
      (this.sharedService.registrationCycleLogId != null
        ? String(this.sharedService.registrationCycleLogId)
        : undefined)
    );
  }

  /** User (mobile) for verification: from verificationData (after submit) or sharedService (init) */
  get inlineUserForVerification(): string | undefined {
    return this.verificationData?.user ?? this.sharedService.userData?.MobileNumber;
  }

  /** Returns true when the password control fails required/length validation */
  get isPasswordInvalid(): boolean {
    const control = this.form.get('Password');
    return !!(
      control &&
      (control.hasError('required') ||
        control.hasError('minlength') ||
        control.hasError('maxlength'))
    );
  }

  /** Verification step rendered as full main content (mobile / lt-md only). */
  get showVerificationInline(): boolean {
    return this.verifyStep && this.isSmallViewport;
  }

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    private salesChatService: SalesChatService,
    private readonly autoRegistrationService: AutoRegistrationService,
    private readonly router: Router,
    private readonly sharedService: SharedService,
    private readonly toastr: ToastrService,
    @Inject(CONFIG) public config: AutoRegistrationConfig,
    public readonly dialog: MatDialog,
    public readonly translateService: TranslateService,
    readonly route: ActivatedRoute,
    readonly title: Title,
    private _renderer2: Renderer2,
    @Inject(DOCUMENT) private _document: Document,
  ) {
    super();

    if (localStorage.getItem('lang') === 'ar') {
      title.setTitle('المرحلة الأولى | التسجيل في جلاميرا بيزنس');
    } else {
      title.setTitle('Phase one | Glamera business registeration');
    }

    translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((_) => {
      this.dir = _.lang;
      title.setTitle(translateService.instant('Title_phase_one'));
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngOnInit(): void {
    this.isSmallViewport = this.breakpointObserver.isMatched(VERIFICATION_INLINE_MAX_WIDTH);
    this.breakpointObserver
      .observe(VERIFICATION_INLINE_MAX_WIDTH)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.isSmallViewport = state.matches;
        this.syncVerificationSidenavForViewport();
      });

    this.isVerifyRoute = this.router.url.includes(this.config.routes.VERIFY);
    this.initForm();
    this.getCountriesList();
    this.salesChatService.chatScript();
    this.autoRegistrationScript();
    this.loadPhasesThenOpenVerificationIfNeeded();
  }

  /** Open verification sidenav if sharedService state matches (no getRegisteredPhases call). */
  private loadPhasesThenOpenVerificationIfNeeded(): void {
    const hasPhasesInService = this.sharedService.userData != null;
    if (hasPhasesInService) {
      const ud = this.sharedService.userData;
      this.form.get('FirstName')?.setValue(ud.FirstName);
      this.form.get('Email')?.setValue(ud.Email);
      this.form.get('MobileNumber')?.setValue(ud.MobileNumber);
      this.form.get('CountryId')?.setValue(ud.CountryId);
      this.form.get('AgreeToPrivacyPolicy')?.setValue(ud.AgreeToPrivacyPolicy);
    }
    this.openVerificationFromSharedServiceIfNeeded();
  }

  /** If sharedService has only user data (mobile not verified, no basic/business details), set verifyStep so sidenav opens in ngAfterViewInit */
  private openVerificationFromSharedServiceIfNeeded(): void {
    const ud = this.sharedService.userData;
    const hasUserData = !!(ud?.MobileNumber ?? ud);
    const onlyPhaseOne =
      hasUserData &&
      !this.sharedService.basicDetails &&
      !this.sharedService.businessDetails;
      
    if (!this.sharedService.isMobileNumberVerified && onlyPhaseOne) {
      this.termsStep = false;
      this.resetStep = false;
      this.verifyStep = true;
      return;
    }

    // If current route is reset-password, open reset password in sidenav
    const isResetPasswordRoute = this.router.url.includes(
      this.config.routes.RESET_PASSWORD
    );

    if (isResetPasswordRoute) {
      this.verifyStep = false;
      this.termsStep = false;
      this.resetStep = true;
      return;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (!this.verificationSidenav) {
        return;
      }
      if (this.verifyStep) {
        this.syncVerificationSidenavForViewport();
      } else if (this.resetStep || this.termsStep) {
        this.verificationSidenav.open();
      }
    }, 0);
  }

  /** On large screens verification uses the sidenav; on small screens it stays inline in main content. */
  private syncVerificationSidenavForViewport(): void {
    if (!this.verificationSidenav || !this.verifyStep) {
      return;
    }
    if (this.isSmallViewport) {
      this.verificationSidenav.close();
    } else {
      this.verificationSidenav.open();
    }
  }

  /** Builds the sign-up form with validators for all fields */
  initForm(): void {
    this.form = new UntypedFormGroup({
      FirstName: new UntypedFormControl(null, [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50)
      ]),
      Email: new UntypedFormControl(null, [
        Validators.required,
        Validators.email,
      ]),
      MobileNumber: new UntypedFormControl(null, [
        Validators.required,
        Validators.maxLength(12),
        Validators.minLength(4),
      ]),
      CountryId: new UntypedFormControl(null, [Validators.required]),
      Password: new UntypedFormControl(null, [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(20),
        Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d).+$/),
      ]),
      AgreeToPrivacyPolicy: new UntypedFormControl(false),
      SavePhasesDataType: new UntypedFormControl(SavePhasesDataType.Add),
    });
  }

  /** Opens the video modal in a dialog */
  onOpenVideo(): void {
    this.dialog.open(VideoComponent, {
      minWidth: '60%',
      maxWidth: '100%',
      panelClass: 'aspect_ratio',
    });
  }

  /** Fetches countries list and sets default country if none selected */
  private getCountriesList(): void {
    this.countriesLoading = true;
    this.sharedService
      .getCountries()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.countriesLoading = false))
      )
      .subscribe((res) => {
        this.countries = res.Result ?? [];
        if (!this.form.get('CountryId')?.value) {
          this.form.patchValue({
            CountryId: this.countries.filter(
              (country) => country.IsCurrentCountry
            )[0]?.Id,
          });
        }
      });
  }

  /** Returns the country object for the currently selected CountryId */
  getSelectedCountry(): COUNTRY {
    return this.countries.filter(
      (country) => country.Id === this.form.get('CountryId')?.value
    )[0];
  }

  /** Opens the privacy policy / terms & conditions in sidenav */
  public onOpenPrivacy(event: Event): void {
    event.preventDefault();
    this.verifyStep = false;
    this.termsStep = true;
    this.verificationSidenav?.open();
  }

  /** Handles form submit: validates privacy policy, calls signUpPhaseOne, then opens verification sidenav on success */
  onSubmit(): void {
    if (!this.form.get('AgreeToPrivacyPolicy')?.value) {
      this.toastr.warning(this.translateService.instant('PRIVACY_ERROR'));
      return;
    }
    this.loading = true;
    // Prepare payload and normalize mobile number (remove leading zero if exists)
    const rawMobile = this.form.get('MobileNumber')?.value?.toString() ?? '';
    const normalizedMobile: string =
      rawMobile.startsWith('0') && rawMobile.length > 1
        ? rawMobile.substring(1)
        : rawMobile;

    const payload = {
      ...this.form.value,
      MobileNumber: normalizedMobile,
    };
    
    this.autoRegistrationService
      .signUpPhaseOne(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.loading = false;
        this.checkResponse = this.sharedService.checkResponseStatus(
          res.Message,
          res.State
        );
        if (this.checkResponse) {
          this.sharedService.setCookie('RegistrationCycleLogId', res.Result.RegistrationCycleLogId!, 30);
          
          // Open sidenav and pass data to verification
          this.verificationData = {
            RegistrationCycleLogId: String(res.Result.RegistrationCycleLogId),
            user: this.form.get('MobileNumber')?.value,
          };
          this.termsStep = false;
          this.verifyStep = true;
          this.sharedService.playSound('assets/sounds/done.mp3');
          this.syncVerificationSidenavForViewport();
        } else {
          if (
            res.Result.IsRegisteredBefore &&
            res.Result.IsMobileNumberVerified
          ) {
            this.router.navigate(['/']);
          }
        }
      });
  }

  /** Injects PageSense analytics script into the document */
  autoRegistrationScript() {
    const script = this._renderer2.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://cdn.pagesense.io/js/deskglamera/6994e7278e9e4769b1aee305339a2f69.js';
    script.defer = true;

    this._renderer2.appendChild(this._document.body, script);
  }

  /** Closes the verification sidenav and resets verifyStep */
  onCloseSidenav(): void {
    this.verificationSidenav?.close();
    this.verifyStep = false;
  }

  /** Closes the terms sidenav and resets termsStep */
  onCloseTermsSidenav(): void {
    this.verificationSidenav?.close();
    this.termsStep = false;
  }

  /** Closes the reset password sidenav and resets resetStep */
  onCloseResetSidenav(): void {
    this.verificationSidenav?.close();
    this.resetStep = false;
  }

  onCloseSidebarFromVerification(): void {
    this.verificationSidenav?.close();

    const registrationCycleLogId =
      this.verificationData?.RegistrationCycleLogId ??
      this.inlineRegistrationCycleLogIdForVerification ??
      (this.sharedService.registrationCycleLogId != null
        ? String(this.sharedService.registrationCycleLogId)
        : this.sharedService.getCookie('RegistrationCycleLogId') ?? undefined);

    console.log('onCloseSidebarFromVerification regId:', registrationCycleLogId);
    console.log('verificationData:',this.verificationData);

    if (registrationCycleLogId) {
      this.router.navigate(['choose-business']);
    }
  }
}
